'use client';

// Lecteur d'animation JRAF (web, iOS, Android : même composant).
//
//   - la timeline est PURE (`@jeraisonne/animation`) : à chaque image, l'état
//     des objets vient de `sceneStateAt(scène, t)` et la scène l'écrit dans le
//     DOM par références — pas de re-rendu React par image ;
//   - l'horloge s'arrête aux portes (question, outil) et attend la voix au
//     début de chaque phrase (`advanceSceneTime`) : l'image suit la voix ;
//   - et la voix attend l'image : elle ne commence une phrase que quand
//     l'horloge est à son début, étiré par le calendrier pour laisser voir
//     les étapes de la précédente (calculs : ~2,5 s de lecture) ;
//   - une scène finit quand ses étapes (lues) ET sa narration sont finies ;
//   - voix : enregistrée si la scène en a une, sinon voix du navigateur,
//     sinon sous-titres seuls ;
//   - partie payante : le document reçu n'a déjà plus ses scènes « À toi »
//     sans accès (`stripInteractive`, côté serveur) ; `locked` les annonce
//     et la carte « Débloquer » appelle `onUnlock` ;
//   - intro (« JeRaisonne présente ») et générique ajoutés ici
//     (`withBookends`), jamais écrits par l'IA ; une scène finie reste un
//     instant à l'écran avant la suivante (`SCENE_END_HOLD_MS`).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CaptionsIcon,
  CaptionsOffIcon,
  Lock,
  PauseIcon,
  PlayIcon,
  RotateCcw,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
} from 'lucide-react';
import {
  activeGate,
  advanceSceneTime,
  bookendOf,
  captionIndex,
  compileAnimation,
  isSceneComplete,
  JRAF_PALETTE,
  nextSentenceStart,
  outroMessage,
  readyToAdvance,
  sceneSpeaker,
  sceneVoice,
  SCENE_END_HOLD_MS,
  sceneStateAt,
  spokenText,
  splitSentences,
  unresolvedGates,
  voiceMayStart,
  voicePosition,
  voiceSpeakers,
  withBookends,
  type BookendMode,
  type CompiledScene,
  type JrafDocument,
  type JrafStep,
  type LockedScene,
} from '@jeraisonne/animation';
import { stripMath } from '@jeraisonne/db/cards';
import { levelLabel, subjectLabel } from '@jeraisonne/db/curriculum';
import { usePrefersReducedMotion } from '../../hooks/use-prefers-reduced-motion';
import { cn } from '../../lib/utils';
import { AnimationMathText } from './animation-math';
import { BookendLayer, type BookendHandle } from './bookend-layer';
import {
  bookendHosts,
  LiveSpeakerAvatar,
  usableAvatar,
  type SpeakerAvatars,
} from './speaker-avatar';
import { HookLayer } from './hook-layer';
import {
  createSoundBackend,
  Soundtrack,
  soundtrackKey,
  type AnimationSoundtrack,
  type SoundBackend,
  type SoundtrackScene,
} from './soundtrack';

export type { AnimationSoundtrack, SoundtrackSource } from './soundtrack';
import { QuestionPanel, ToolPanel } from './interactive-panels';
import { SCENE_TARGET, SCENE_TEXT } from './scene-sizes';
import {
  AudioNarrator,
  BrowserNarrator,
  hasSpeechSynthesis,
  type Narrator,
  type SceneNarration,
} from './narrators';
import { AnimationStage, type StageHandle } from './stage';

export interface FrameScheduler {
  request(cb: (now: number) => void): number;
  cancel(id: number): void;
}

const browserScheduler: FrameScheduler = {
  request: (cb) => requestAnimationFrame(cb),
  cancel: (id) => cancelAnimationFrame(id),
};

export interface AnimationPlayerProps {
  /** document VALIDÉ ; sans accès, déjà privé de sa partie interactive */
  doc: JrafDocument;
  /** scènes « À toi » retirées (partie payante) */
  locked?: readonly LockedScene[];
  /** carte « Débloquer » ; absent : la carte l'annonce sans bouton (app iOS) */
  onUnlock?: () => void;
  /** ouvre un outil de la boîte à outils (`tool.open`) */
  onOpenTool?: (toolId: string) => void;
  /** URL d'une ressource du bucket (images, voix enregistrées) */
  assetUrl?: (path: string) => string;
  parameterValues?: Record<string, number>;
  /**
   * intro et générique : `app` (défaut), `video` (générique de publication :
   * « Retrouve d'autres leçons… »), `false` : sans (aperçu d'une scène)
   */
  bookends?: BookendMode | false;
  className?: string;
  /** scène jouée (intro comprise : index dans la liste jouée) */
  onSceneChange?: (index: number, sceneId: string) => void;
  /** premier « Lancer » (mesure des lectures) ; « Revoir » ne le rappelle pas */
  onStart?: () => void;
  onComplete?: () => void;
  /** musique d'ambiance et jingles (bibliothèque audio, choisis en base) */
  soundtrack?: AnimationSoundtrack | null;
  /** avatars animés des comptes qui ont enregistré les voix (`speakerId`) */
  speakerAvatars?: SpeakerAvatars | null;
  /** tests : horloge, narrateurs et sortie sonore injectés */
  scheduler?: FrameScheduler;
  createNarrator?: (kind: 'browser' | 'audio') => Narrator | null;
  createSoundBackend?: () => SoundBackend | null;
}

const HORA_LABEL: Record<string, string> = {
  H: 'Hypothèse',
  O: 'Outil',
  R: 'Raisonnement',
  A: 'Application',
};
const HORA_COLOR: Record<string, string> = {
  H: JRAF_PALETTE['hora-h'],
  O: JRAF_PALETTE['hora-o'],
  R: JRAF_PALETTE['hora-r'],
  A: JRAF_PALETTE['hora-a'],
};
/** texte de la pastille HORA : contraste suffisant sur chaque teinte */
const HORA_TEXT: Record<string, string> = {
  H: JRAF_PALETTE.paper,
  O: JRAF_PALETTE.night,
  R: JRAF_PALETTE.night,
  A: JRAF_PALETTE.paper,
};

function defaultNarrator(kind: 'browser' | 'audio'): Narrator | null {
  if (typeof window === 'undefined') return null;
  if (kind === 'audio') return typeof Audio === 'function' ? new AudioNarrator() : null;
  return hasSpeechSynthesis() ? new BrowserNarrator() : null;
}

export function AnimationPlayer({
  doc,
  locked = [],
  onUnlock,
  onOpenTool,
  assetUrl,
  parameterValues,
  bookends = 'app',
  className,
  onSceneChange,
  onStart,
  onComplete,
  soundtrack,
  speakerAvatars,
  scheduler = browserScheduler,
  createNarrator = defaultNarrator,
  createSoundBackend: soundBackendFactory = createSoundBackend,
}: AnimationPlayerProps) {
  // réglages comparés par valeur : un objet écrit en ligne ne recompile pas à chaque rendu
  const paramsKey = JSON.stringify(parameterValues ?? {});
  // document joué : [accroche (vidéo)] + intro + scènes + générique
  const played = useMemo(
    () => (bookends ? withBookends(doc, { mode: bookends }) : doc),
    [doc, bookends],
  );
  const anim = useMemo(
    () =>
      compileAnimation(played, {
        parameterValues: JSON.parse(paramsKey) as Record<string, number>,
      }),
    [played, paramsKey],
  );
  /** scènes du document (sans l'intro ni le générique) : compteur et barre de progression */
  const contentIndexes = useMemo(
    () => anim.scenes.filter((s) => !bookendOf(s.scene.id)).map((s) => s.index),
    [anim],
  );
  const bookendInfo = useMemo(() => {
    const level = doc.metadata.levels[0];
    const meta = [level ? levelLabel(level) : '', subjectLabel(doc.metadata.subject)]
      .filter(Boolean)
      .join(' · ');
    return {
      meta: meta || null,
      outro: outroMessage(doc, bookends || 'app'),
      hook: bookends === 'video' ? (doc.bookends?.hook ?? null) : null,
      speakers: voiceSpeakers(played),
    };
  }, [doc, played, bookends]);
  /** intro et générique : avatars de tous les comptes qui ont enregistré une voix */
  const hostAvatars = useMemo(() => bookendHosts(played, speakerAvatars), [played, speakerAvatars]);
  const reducedMotion = usePrefersReducedMotion();
  const stageRef = useRef<StageHandle>(null);
  const bookendRef = useRef<BookendHandle>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLElement>(null);
  const playButtonRef = useRef<HTMLButtonElement>(null);
  // rappels du parent : lus par référence (une fonction en ligne ne relance pas la boucle)
  const callbacks = useRef({ onSceneChange, onStart, onComplete, assetUrl });
  callbacks.current = { onSceneChange, onStart, onComplete, assetUrl };

  const [sceneIndex, setSceneIndex] = useState(0);
  const [started, setStarted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [ended, setEnded] = useState(false);
  const [waitingNext, setWaitingNext] = useState(false);
  const [gateId, setGateId] = useState<string | null>(null);
  const [caption, setCaption] = useState(0);
  const [soundOn, setSoundOn] = useState(true);
  const [captionsOn, setCaptionsOn] = useState(doc.player.captions);
  /** puce « étape HORA » de la scène qui commence */
  const [chip, setChip] = useState<{ index: number; visible: boolean } | null>(null);

  // état de lecture lu par la boucle (pas de re-rendu par image)
  const tRef = useRef(0);
  const resolvedRef = useRef(new Set<string>());
  const narrationDoneRef = useRef(false);
  /** phrase commencée par la voix ; `null` : pas de voix (horloge seule) */
  const voiceRef = useRef<number | null>(null);
  /** début de la phrase au bord de laquelle la voix attend (elle a fini la précédente) */
  const voiceReachedRef = useRef<number | null>(null);
  /** temps de scène où elle s'est finie (temps de lecture avant la suivante) */
  const completeAtRef = useRef<number | null>(null);
  const sceneRef = useRef(0);
  sceneRef.current = sceneIndex;
  const soundRef = useRef(true);

  const narrators = useRef<{ browser?: Narrator | null; audio?: Narrator | null }>({});
  /** la voix a été mise en pause par une question ouverte */
  const gatePausedRef = useRef(false);
  /** voix remise pendant une question : elle reprendra à « Continuer », à cette phrase */
  const voicePendingRef = useRef<number | null>(null);
  const narratorOf = useCallback(
    (kind: 'browser' | 'audio') => {
      if (!(kind in narrators.current)) narrators.current[kind] = createNarrator(kind);
      return narrators.current[kind] ?? null;
    },
    [createNarrator],
  );
  const activeNarrator = useRef<Narrator | null>(null);

  // bande-son : créée au premier lancement (geste), refaite si les sons changent
  const trackKey = soundtrackKey(soundtrack);
  const trackSource = useRef(soundtrack);
  trackSource.current = soundtrack;
  const trackRef = useRef<{ key: string; track: Soundtrack } | null>(null);
  const soundtrackOf = useCallback((): Soundtrack | null => {
    const cur = trackRef.current;
    if (cur && cur.key === trackKey) return cur.track;
    cur?.track.dispose();
    trackRef.current = null;
    const src = trackSource.current;
    if (!src || (!src.music && !src.jingleIntro && !src.jingleOutro)) return null;
    const backend = soundBackendFactory();
    if (!backend) return null;
    const track = new Soundtrack(src, backend);
    track.setMuted(!soundRef.current);
    trackRef.current = { key: trackKey, track };
    return track;
  }, [trackKey, soundBackendFactory]);
  const sceneSound = useCallback(
    (index: number): SoundtrackScene => {
      const kind = bookendOf(anim.scenes[index]?.scene.id ?? '');
      return kind ?? 'content';
    },
    [anim],
  );

  // un nouveau document peut avoir moins de scènes : jamais d'index hors limites
  const scene: CompiledScene = anim.scenes[sceneIndex] ?? anim.scenes[0]!;
  const sentences = useMemo(() => splitSentences(scene.scene.narration.text), [scene]);

  const stopVoice = useCallback(() => {
    activeNarrator.current?.stop();
    activeNarrator.current = null;
  }, []);

  /** démarre la voix de la scène à la phrase `from` (ou l'horloge seule) */
  const startVoice = useCallback(
    (index: number, from: number) => {
      stopVoice();
      const sc = anim.scenes[index]!;
      const n = sc.scene.narration;
      const audioAsset = n.audioAssetId
        ? anim.doc.assets.find((a) => a.id === n.audioAssetId)
        : undefined;
      const assetUrl = callbacks.current.assetUrl;
      const narration: SceneNarration = {
        sentences: splitSentences(spokenText(n)),
        audio:
          audioAsset && assetUrl
            ? {
                src: assetUrl(audioAsset.path),
                sentenceStartsMs: sc.voiceStartsMs,
                offsetMs: sc.voiceOffsetMs,
                endMs: sc.voiceTotalMs,
              }
            : undefined,
      };
      const narrator = soundRef.current ? narratorOf(narration.audio ? 'audio' : 'browser') : null;
      if (!narrator) {
        voiceRef.current = null;
        return null;
      }
      activeNarrator.current = narrator;
      voiceRef.current = from - 1;
      voiceReachedRef.current = null;
      narrator.start(narration, from, {
        onSentence: (i) => {
          if (sceneRef.current !== index || activeNarrator.current !== narrator) return;
          voiceRef.current = i;
          voiceReachedRef.current = null;
          setCaption(i);
        },
        onEnd: () => {
          if (sceneRef.current !== index || activeNarrator.current !== narrator) return;
          narrationDoneRef.current = true;
          voiceRef.current = sc.sentenceStartsMs.length;
        },
        onFail: () => {
          if (activeNarrator.current !== narrator) return;
          // pas de voix utilisable : sous-titres au rythme de l'horloge
          activeNarrator.current = null;
          voiceRef.current = null;
          // voix du navigateur absente de l'appareil : on ne la retente plus
          if (narrator.kind === 'browser') narrators.current.browser = null;
        },
        onInterrupt: () => {
          if (activeNarrator.current !== narrator) return;
          // son coupé par le système (appel, casque) : pause, « Lecture » reprend
          setPlaying(false);
        },
        // la voix attend l'image : une phrase commence quand l'horloge est à son
        // début, et après la réponse à une question qui la précède
        mayStart: (i) => {
          if (sceneRef.current !== index || activeNarrator.current !== narrator) return false;
          const gates = unresolvedGates(sc, resolvedRef.current).map((g) => g.startMs);
          const ok = voiceMayStart(sc.sentenceStartsMs, i, tRef.current, gates);
          // elle a fini la phrase d'avant : une question posée pendant celle-ci peut s'ouvrir
          if (!ok) voiceReachedRef.current = sc.sentenceStartsMs[i] ?? null;
          return ok;
        },
      });
      return narrator.kind;
    },
    [anim, narratorOf, stopVoice],
  );

  const showScene = useCallback(
    (index: number, t = 0) => {
      const sc = anim.scenes[index]!;
      stageRef.current?.apply(sceneStateAt(sc, t, { reducedMotion }));
      bookendRef.current?.apply(t);
    },
    [anim, reducedMotion],
  );

  const goScene = useCallback(
    (index: number, keepPlaying: boolean, withSound = keepPlaying) => {
      stopVoice();
      tRef.current = 0;
      resolvedRef.current = new Set();
      narrationDoneRef.current = false;
      voiceRef.current = null;
      voiceReachedRef.current = null;
      completeAtRef.current = null;
      sceneRef.current = index;
      setSceneIndex(index);
      setGateId(null);
      setCaption(0);
      setWaitingNext(false);
      setEnded(false);
      setChip({ index, visible: true });
      showScene(index, 0);
      gatePausedRef.current = false;
      voicePendingRef.current = null;
      callbacks.current.onSceneChange?.(index, anim.scenes[index]!.scene.id);
      if (keepPlaying) startVoice(index, 0);
      // jingle de l'intro, du générique (la musique s'éteint au générique)
      if (withSound) trackRef.current?.track.scene(sceneSound(index));
    },
    [anim, sceneSound, showScene, startVoice, stopVoice],
  );

  // nouveau document (ou nouveaux réglages) : on repart du début, voix arrêtée
  const firstAnim = useRef(anim);
  useEffect(() => {
    if (firstAnim.current === anim) return;
    firstAnim.current = anim;
    stopVoice();
    setPlaying(false);
    setStarted(false);
    tRef.current = 0;
    resolvedRef.current = new Set();
    narrationDoneRef.current = false;
    voiceRef.current = null;
    completeAtRef.current = null;
    sceneRef.current = 0;
    setSceneIndex(0);
    setGateId(null);
    setCaption(0);
    setWaitingNext(false);
    setEnded(false);
    setChip(null);
    stageRef.current?.apply(sceneStateAt(anim.scenes[0]!, 0, { reducedMotion }));
  }, [anim, reducedMotion, stopVoice]);

  // ------------------------------------------------------------ boucle
  useEffect(() => {
    if (!playing) return;
    let last: number | null = null;
    let id = 0;
    const tick = (now: number) => {
      const dt = last === null ? 0 : Math.min(100, Math.max(0, now - last));
      last = now;
      const index = sceneRef.current;
      const sc = anim.scenes[index]!;
      const resolved = resolvedRef.current;
      const voice = voiceRef.current;
      const narrationMs = sc.schedule.narrationMs;
      const t = advanceSceneTime(tRef.current, dt, {
        gates: unresolvedGates(sc, resolved).map((g) => g.startMs),
        // la voix pas finie retient l'image (y compris à la fin de la dernière phrase)
        nextSentenceStart:
          voice === null || narrationDoneRef.current
            ? null
            : nextSentenceStart(sc.sentenceStartsMs, voice, narrationMs),
        voiceSentenceStart:
          voice === null ? null : voicePosition(sc.sentenceStartsMs, voice, narrationMs),
      });
      tRef.current = t;
      stageRef.current?.apply(sceneStateAt(sc, t, { reducedMotion }));
      bookendRef.current?.apply(t);
      if (progressRef.current) {
        const p = Math.min(1, t / Math.max(1, sc.schedule.endMs));
        progressRef.current.style.transform = `scaleX(${p})`;
      }
      if (voice === null) {
        setCaption(captionIndex(sc.sentenceStartsMs, t, null));
        if (t >= sc.schedule.narrationMs) narrationDoneRef.current = true;
      }
      // une porte attend aussi la voix (une question après la narration : sa fin réelle)
      const gate = activeGate(
        sc,
        t,
        resolved,
        voice === null
          ? null
          : narrationDoneRef.current
            ? Number.POSITIVE_INFINITY // voix finie : plus rien à attendre
            : Math.max(
                voicePosition(sc.sentenceStartsMs, voice, narrationMs) ?? 0,
                // la voix attend au bord de la phrase suivante : elle a fini celle de la question
                voiceReachedRef.current ?? 0,
              ),
      );
      setGateId(gate?.id ?? null);
      // une question ouverte fait taire la voix (elle ne dit pas la suite, ni la réponse)
      if (gate && !gatePausedRef.current && activeNarrator.current) {
        gatePausedRef.current = true;
        activeNarrator.current.pause();
      }
      const isLast = index >= anim.scenes.length - 1;
      if (
        completeAtRef.current === null &&
        isSceneComplete(sc, t, narrationDoneRef.current, resolved)
      )
        completeAtRef.current = t;
      // temps de lecture avant la scène suivante (pas avant la fin, ni pour un passage manuel)
      // (l'accroche d'une vidéo coupe net sur l'intro : pas de temps mort)
      const hold =
        isLast || sc.scene.advance.mode === 'manual' || bookendOf(sc.scene.id) === 'hook'
          ? 0
          : SCENE_END_HOLD_MS;
      if (readyToAdvance(completeAtRef.current, t, hold)) {
        if (!isLast) {
          if (sc.scene.advance.mode === 'auto') {
            goScene(index + 1, true);
          } else {
            // l'image s'arrête : sans éclairage qui resterait figé
            stageRef.current?.apply(sc.end);
            setWaitingNext(true);
            setPlaying(false);
            return;
          }
        } else {
          stopVoice();
          trackRef.current?.track.finish();
          stageRef.current?.apply(sc.end);
          setEnded(true);
          setPlaying(false);
          callbacks.current.onComplete?.();
          return;
        }
      }
      id = scheduler.request(tick);
    };
    id = scheduler.request(tick);
    return () => scheduler.cancel(id);
  }, [anim, goScene, playing, reducedMotion, scheduler, stopVoice]);

  // la puce « étape HORA » s'efface après un moment
  useEffect(() => {
    if (!chip?.visible) return;
    const timer = setTimeout(() => setChip((c) => (c ? { ...c, visible: false } : c)), 2200);
    return () => clearTimeout(timer);
  }, [chip]);

  // arrêt de la voix et libération des lecteurs au démontage
  useEffect(
    () => () => {
      stopVoice();
      narrators.current.browser?.dispose?.();
      narrators.current.audio?.dispose?.();
      trackRef.current?.track.dispose();
      trackRef.current = null;
    },
    [stopVoice],
  );

  // la bande-son suit la lecture : pause, reprise (son coupé par le système compris)
  useEffect(() => {
    if (!started) return;
    const track = trackRef.current?.track;
    if (playing) track?.resume();
    else track?.pause();
  }, [playing, started]);

  // focus : le bouton « Lecture » remplace celui de l'écran de départ
  useEffect(() => {
    if (started) playButtonRef.current?.focus({ preventScroll: true });
  }, [started]);

  // ---------------------------------------------------------- commandes
  const play = () => {
    if (ended) {
      if (doc.player.allowReplay) restart();
      return;
    }
    if (!started) {
      // DANS le geste : iOS n'autorise la voix et le son qu'ainsi. La voix
      // qui parle maintenant est déverrouillée en parlant ; l'autre, à vide.
      narratorOf('audio')?.unlock();
      const used = startVoice(sceneIndex, 0);
      if (used !== 'browser') narratorOf('browser')?.unlock();
      // musique et jingle de l'intro : dans le geste aussi
      soundtrackOf()?.begin(sceneSound(sceneIndex));
      setStarted(true);
      setChip({ index: sceneIndex, visible: true });
      callbacks.current.onStart?.();
    } else if (waitingNext) {
      goScene(sceneIndex + 1, true);
    } else if (activeNarrator.current) {
      // une question ouverte garde la voix en pause jusqu'à « Continuer »
      if (!gatePausedRef.current) activeNarrator.current.resume();
    } else if (voiceRef.current === null && soundRef.current && !narrationDoneRef.current) {
      startVoice(sceneIndex, captionIndex(scene.sentenceStartsMs, tRef.current, null));
    }
    setPlaying(true);
  };

  const pause = () => {
    activeNarrator.current?.pause();
    setPlaying(false);
  };

  const restart = () => {
    setStarted(true);
    soundtrackOf()?.begin(sceneSound(0));
    goScene(0, true, false);
    setPlaying(true);
    playButtonRef.current?.focus({ preventScroll: true });
  };

  const toScene = (index: number) => {
    if (index < 0 || index >= anim.scenes.length) return;
    if (!started) {
      // commencée par « Scène suivante » : même déverrouillage du son (iOS) et même mesure
      narratorOf('audio')?.unlock();
      narratorOf('browser')?.unlock();
      callbacks.current.onStart?.();
    }
    setStarted(true);
    goScene(index, playing || ended || waitingNext);
    if (ended || waitingNext) setPlaying(true);
  };

  const toggleSound = () => {
    const next = !soundRef.current;
    soundRef.current = next;
    setSoundOn(next);
    trackRef.current?.track.setMuted(!next);
    if (!next) {
      stopVoice();
      voiceRef.current = null;
      voicePendingRef.current = null;
    } else if (gateId !== null || gatePausedRef.current) {
      // une question est ouverte : la voix ne dit pas la suite avant « Continuer »
      voicePendingRef.current = caption;
    } else if (playing && !narrationDoneRef.current) {
      // reprend la phrase en cours, avec la voix
      startVoice(sceneRef.current, caption);
    }
  };

  const resolveGate = (id: string) => {
    resolvedRef.current.add(id);
    setGateId(null);
    // la voix reprend (la phrase interrompue, depuis son début)
    const pending = voicePendingRef.current;
    const wasPaused = gatePausedRef.current;
    voicePendingRef.current = null;
    gatePausedRef.current = false;
    if (pending !== null) {
      if (playing && !narrationDoneRef.current) startVoice(sceneRef.current, pending);
    } else if (wasPaused && playing) {
      activeNarrator.current?.resume();
    }
    rootRef.current?.focus({ preventScroll: true });
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const onButton = (e.target as HTMLElement).closest('button');
    if ((e.key === ' ' || e.key === 'k') && !onButton) {
      e.preventDefault();
      if (playing) pause();
      else play();
    } else if (e.key === 'ArrowRight' && doc.player.allowNext) toScene(sceneIndex + 1);
    else if (e.key === 'ArrowLeft' && doc.player.allowPrevious) toScene(sceneIndex - 1);
  };

  // ------------------------------------------------------------- rendu
  const gateStep: JrafStep | null = gateId
    ? (scene.scene.steps.find((s) => s.id === gateId) ?? null)
    : null;
  const captionText = sentences[caption] ?? '';
  const showLocked = ended && locked.length > 0;
  const chipScene = chip ? anim.scenes[chip.index]?.scene : undefined;
  const hora = chipScene?.hora;
  const { width: W, height: H } = doc.canvas;
  const bookend = bookendOf(scene.scene.id);
  // avatar de qui a enregistré la voix de la scène (jamais la synthèse)
  const sceneAvatar = bookend
    ? null
    : usableAvatar(speakerAvatars, sceneSpeaker(anim.doc, scene.scene.id));
  const sceneWords = sceneAvatar ? sceneVoice(anim.doc, scene.scene.id)?.words : undefined;
  const showAvatar = !!sceneAvatar && started && !ended;
  // compteur « n / N » et barre : scènes du document seulement
  const contentPos = Math.max(1, contentIndexes.filter((i) => i <= sceneIndex).length);
  const contentCount = contentIndexes.length;

  const overlay = (
    <>
      {/* fini : l'écran de fin remplace le générique (sinon ses textes transparaissent) */}
      {bookend === 'hook' && bookendInfo.hook && !ended && (
        <HookLayer
          key="hook"
          ref={bookendRef}
          text={bookendInfo.hook.text}
          emphasis={bookendInfo.hook.emphasis}
          reducedMotion={reducedMotion}
        />
      )}
      {bookend && bookend !== 'hook' && !ended && (
        <BookendLayer
          key={bookend}
          ref={bookendRef}
          kind={bookend}
          title={doc.title}
          meta={bookendInfo.meta}
          message={bookendInfo.outro}
          speakers={bookendInfo.speakers}
          hosts={hostAvatars}
          reducedMotion={reducedMotion}
        />
      )}

      {chipScene && started && !bookendOf(chipScene.id) && (
        <div
          className={cn(
            'absolute left-4 top-4 flex items-center',
            showAvatar ? 'max-w-[calc(100%-7rem)]' : 'max-w-[85%]',
          )}
          data-chip-row=""
        >
          <div
            className="flex min-w-0 max-w-full items-center gap-2 rounded-full py-1.5 pl-1.5 pr-4 text-[15px] font-semibold"
            style={{
              backgroundColor: 'rgba(0,0,0,0.45)',
              color: JRAF_PALETTE.paper,
              opacity: chip?.visible ? 1 : 0,
              transition: reducedMotion ? undefined : 'opacity 400ms ease',
            }}
            aria-hidden={!chip?.visible}
          >
            {hora && (
              <span
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[14px] font-bold"
                style={{
                  backgroundColor: HORA_COLOR[hora],
                  color: HORA_TEXT[hora],
                  boxShadow: '0 0 0 2px rgba(255,255,255,0.9)',
                }}
                title={HORA_LABEL[hora]}
              >
                {hora}
              </span>
            )}
            <span className="truncate">
              <AnimationMathText>{chipScene.title}</AnimationMathText>
            </span>
          </div>
        </div>
      )}

      {showAvatar && sceneAvatar && (
        <div className="absolute right-4 top-4">
          <LiveSpeakerAvatar
            key={scene.scene.id}
            frames={sceneAvatar.frames}
            words={sceneWords}
            voiceTime={() => activeNarrator.current?.voiceTime?.() ?? null}
            still={reducedMotion}
          />
        </div>
      )}

      {captionsOn && started && !gateStep && !ended && !bookend && captionText && (
        <div className="absolute inset-x-4 bottom-6 flex justify-center">
          <p
            className={cn('rounded-xl px-3 py-2 text-center leading-snug', SCENE_TEXT)}
            style={{ backgroundColor: 'rgba(0,0,0,0.62)', color: JRAF_PALETTE.paper }}
          >
            <AnimationMathText>{captionText}</AnimationMathText>
          </p>
        </div>
      )}

      {gateStep?.action === 'question.ask' && (
        <QuestionPanel key={gateStep.id} step={gateStep} onDone={() => resolveGate(gateStep.id)} />
      )}
      {gateStep?.action === 'tool.open' && (
        <ToolPanel
          key={gateStep.id}
          step={gateStep}
          onOpenTool={onOpenTool}
          onDone={() => resolveGate(gateStep.id)}
        />
      )}

      {!started && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center gap-5 px-6 text-center"
          style={{ backgroundColor: 'rgba(6,20,51,0.55)' }}
        >
          <p className="text-[24px] font-bold leading-tight" style={{ color: JRAF_PALETTE.paper }}>
            <AnimationMathText>{doc.title}</AnimationMathText>
          </p>
          <button
            type="button"
            onClick={play}
            aria-label="Lancer l’animation"
            className="flex h-24 w-24 items-center justify-center rounded-full shadow-2xl focus-visible:outline-none focus-visible:ring-4"
            style={{ backgroundColor: JRAF_PALETTE.gold, color: JRAF_PALETTE.night }}
          >
            <PlayIcon size={44} fill="currentColor" aria-hidden="true" />
          </button>
        </div>
      )}

      {ended && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-6 text-center"
          style={{ backgroundColor: 'rgba(6,20,51,0.88)', color: JRAF_PALETTE.paper }}
        >
          {showLocked ? (
            <>
              <span
                className="flex h-16 w-16 items-center justify-center rounded-full"
                style={{ backgroundColor: JRAF_PALETTE.gold, color: JRAF_PALETTE.night }}
              >
                <Lock size={30} aria-hidden="true" />
              </span>
              <p className="text-[22px] font-bold leading-tight">À toi de jouer !</p>
              <p className="text-[16px] leading-snug opacity-90">
                {locked.length > 1 ? 'Les parties ' : 'La partie '}
                {locked.map((l, i) => (
                  <span key={l.id}>
                    {i > 0 && ', '}« <AnimationMathText>{l.title}</AnimationMathText> »
                  </span>
                ))}
                {locked.length > 1 ? ' se débloquent' : ' se débloque'} avec la SKILLCARD.
              </p>
              {onUnlock && (
                <button
                  type="button"
                  onClick={onUnlock}
                  className={cn(
                    'rounded-xl px-6 font-bold focus-visible:outline-none focus-visible:ring-4',
                    SCENE_TARGET,
                    SCENE_TEXT,
                  )}
                  style={{ backgroundColor: JRAF_PALETTE.gold, color: JRAF_PALETTE.night }}
                >
                  Débloquer
                </button>
              )}
            </>
          ) : (
            <p className="text-[22px] font-bold">C’est fini !</p>
          )}
          {doc.player.allowReplay && (
            <button
              type="button"
              onClick={restart}
              className={cn(
                'flex items-center gap-2 rounded-xl border-2 px-5 font-semibold focus-visible:outline-none focus-visible:ring-4',
                SCENE_TARGET,
                SCENE_TEXT,
              )}
              style={{ borderColor: JRAF_PALETTE.paper }}
            >
              <RotateCcw size={18} aria-hidden="true" /> Revoir
            </button>
          )}
        </div>
      )}
    </>
  );

  const ctrl =
    'flex h-11 w-11 items-center justify-center rounded-full text-ink-700 transition-colors hover:bg-ink-100 focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-none disabled:opacity-35 disabled:hover:bg-transparent';

  return (
    <section
      ref={rootRef}
      className={cn(
        'focus-visible:ring-primary-500 mx-auto flex w-full flex-col items-center gap-2 rounded-2xl outline-none focus-visible:ring-2',
        className,
      )}
      aria-label={stripMath(doc.title)}
      onKeyDown={onKeyDown}
      tabIndex={0}
    >
      <div
        className="w-full"
        style={{
          width: `min(100%, max(260px, calc((100dvh - var(--jr-anim-chrome, 150px)) * ${W} / ${H})))`,
        }}
      >
        <AnimationStage
          ref={stageRef}
          anim={anim}
          assetUrl={assetUrl}
          overlay={overlay}
          className="ring-ink-200 rounded-2xl shadow-lg ring-1"
        />

        {doc.player.showProgress && (
          <div className="mt-2 flex gap-1" aria-hidden="true">
            {contentIndexes.map((i) => (
              <div
                key={anim.scenes[i]!.scene.id}
                className="bg-ink-200 h-1 flex-1 overflow-hidden rounded-full"
              >
                <div
                  // remonté quand la scène courante change : la barre que la
                  // boucle a remplie à la main repart de la valeur de React
                  key={i === sceneIndex ? 'courante' : 'autre'}
                  ref={i === sceneIndex ? progressRef : undefined}
                  className="bg-primary-500 h-full origin-left rounded-full"
                  style={{
                    transform: `scaleX(${i < sceneIndex || (i === sceneIndex && ended) ? 1 : 0})`,
                  }}
                />
              </div>
            ))}
            {locked.map((l) => (
              <div key={l.id} className="bg-ink-100 h-1 flex-1 rounded-full" />
            ))}
          </div>
        )}

        <div className="mt-1 flex items-center justify-between">
          <div className="flex items-center">
            {doc.player.allowPrevious && (
              <button
                type="button"
                className={ctrl}
                onClick={() => toScene(sceneIndex - 1)}
                disabled={sceneIndex === 0}
                aria-label="Scène précédente"
              >
                <SkipBack size={20} aria-hidden="true" />
              </button>
            )}
            <button
              ref={playButtonRef}
              type="button"
              className={cn(ctrl, 'bg-primary-500 hover:bg-primary-600 h-12 w-12 text-white')}
              onClick={playing ? pause : play}
              disabled={ended && !doc.player.allowReplay}
              aria-label={playing ? 'Pause' : ended ? 'Revoir' : 'Lecture'}
            >
              {playing ? (
                <PauseIcon size={22} fill="currentColor" aria-hidden="true" />
              ) : ended ? (
                <RotateCcw size={20} aria-hidden="true" />
              ) : (
                <PlayIcon size={22} fill="currentColor" aria-hidden="true" />
              )}
            </button>
            {doc.player.allowNext && (
              <button
                type="button"
                className={ctrl}
                onClick={() => toScene(sceneIndex + 1)}
                disabled={sceneIndex >= anim.scenes.length - 1}
                aria-label="Scène suivante"
              >
                <SkipForward size={20} aria-hidden="true" />
              </button>
            )}
          </div>
          <p className="text-ink-500 flex items-center gap-1 whitespace-nowrap px-2 text-sm">
            {contentPos} / {contentCount}
            {locked.length > 0 && (
              <span
                className="flex items-center gap-0.5"
                title={`${locked.length} à débloquer`}
                aria-label={`plus ${locked.length} à débloquer`}
              >
                {' '}
                + {locked.length}
                <Lock size={13} aria-hidden="true" />
              </span>
            )}
          </p>
          <div className="flex items-center">
            <button
              type="button"
              className={ctrl}
              onClick={toggleSound}
              aria-pressed={!soundOn}
              aria-label={soundOn ? 'Couper le son' : 'Remettre le son'}
            >
              {soundOn ? (
                <Volume2 size={20} aria-hidden="true" />
              ) : (
                <VolumeX size={20} aria-hidden="true" />
              )}
            </button>
            <button
              type="button"
              className={ctrl}
              onClick={() => setCaptionsOn((c) => !c)}
              aria-pressed={captionsOn}
              aria-label={captionsOn ? 'Masquer les sous-titres' : 'Afficher les sous-titres'}
            >
              {captionsOn ? (
                <CaptionsIcon size={20} aria-hidden="true" />
              ) : (
                <CaptionsOffIcon size={20} aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* lecteurs d'écran : la phrase en cours, en texte */}
      <p className="sr-only" aria-live="polite">
        {started && !ended && !gateStep ? stripMath(captionText) : ''}
      </p>
    </section>
  );
}
